/**
 * StaffPinLogin — Tastiera PIN per accesso rapido operatori (CRM v7).
 *
 * Comportamento:
 *  - 4 pallini indicatori del PIN inserito (mai in chiaro).
 *  - Tastiera 3x4 grande, target ≥ 60px per touch.
 *  - Al quarto tap tenta il login automaticamente.
 *  - PIN errato → shake, reset input, feedback generico.
 *  - Nessuna sidebar, nessun elemento admin.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Delete } from "lucide-react";

const KEYS = ["1","2","3","4","5","6","7","8","9","","0","<"];

export default function StaffPinLogin() {
  const nav = useNavigate();
  const { user, pinLogin, logout } = useAuth();
  const [pin, setPin] = useState("");
  const [shake, setShake] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // If a stale session is present (e.g. leftover bearer token from a
  // previous operator on the same tablet), skip straight to Home.
  // Cross-device recovery: se c'è una route pre-auth-lost salvata,
  // vi reindirizziamo direttamente (draft e stato preservati).
  const consumePostLoginRedirect = () => {
    try {
      const target = sessionStorage.getItem("sb.postLogin.redirect");
      if (target && target.startsWith("/staff") && !target.includes("/login")) {
        sessionStorage.removeItem("sb.postLogin.redirect");
        return target;
      }
    } catch { /* silent */ }
    return null;
  };

  useEffect(() => {
    if (user) {
      const target = consumePostLoginRedirect() || "/staff";
      nav(target, { replace: true });
    }
  }, [user, nav]);

  const doLogin = useCallback(async (val) => {
    setBusy(true); setErr("");
    try {
      await pinLogin(val);
      const target = consumePostLoginRedirect() || "/staff";
      setTimeout(() => nav(target, { replace: true }), 100);
    } catch (e) {
      setShake(true);
      setErr(e?.response?.status === 429
             ? "Troppi tentativi. Attendi qualche minuto."
             : "PIN non riconosciuto");
      setTimeout(() => setShake(false), 400);
      setTimeout(() => setPin(""), 200);
    } finally { setBusy(false); }
  }, [nav, pinLogin]);

  const pinRef = useRef("");
  useEffect(() => { pinRef.current = pin; }, [pin]);
  const tap = (k) => {
    if (busy) return;
    if (k === "<") { setPin((p) => p.slice(0, -1)); return; }
    if (!k || pinRef.current.length >= 4) return;
    const next = (pinRef.current + k).slice(0, 4);
    pinRef.current = next;
    setPin(next);
    if (next.length === 4) doLogin(next);
  };

  // Keyboard support (desktop)
  useEffect(() => {
    const onKey = (e) => {
      if (/^[0-9]$/.test(e.key)) tap(e.key);
      else if (e.key === "Backspace") tap("<");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);

  return (
    <div className="min-h-screen bg-sand-100 flex items-center justify-center px-4 py-8"
          data-testid="staff-pin-page">
      <div className={`w-full max-w-sm ${shake ? "animate-[shake_0.35s]" : ""}`}>
        <div className="text-center mb-8">
          <div className="eyebrow text-[10px]">Sarabi</div>
          <h1 className="font-serif text-3xl mt-1">Staff</h1>
          <p className="text-xs text-ink-500 mt-1">Inserisci il tuo PIN operatore</p>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-4 mb-8" data-testid="pin-dots">
          {[0,1,2,3].map((i) => (
            <div key={i}
                  data-testid={`pin-dot-${i}`}
                  className={`w-4 h-4 rounded-full border-2 transition-all
                    ${i < pin.length
                      ? "bg-bronze-700 border-bronze-700 scale-110"
                      : "border-sand-400 bg-transparent"}`} />
          ))}
        </div>

        {err && (
          <div className="text-center text-sm text-rose-700 mb-4" data-testid="pin-error">
            {err}
          </div>
        )}

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-3">
          {KEYS.map((k, i) => {
            if (!k) return <div key={i} />;
            const isDelete = k === "<";
            return (
              <button key={i}
                       onClick={() => tap(k)}
                       disabled={busy}
                       data-testid={isDelete ? "pin-key-delete" : `pin-key-${k}`}
                       className={`min-h-[70px] rounded-2xl font-serif text-3xl
                                    border-2 border-sand-300 bg-white
                                    active:scale-95 transition-transform
                                    disabled:opacity-40
                                    ${isDelete ? "text-rose-600" : "text-ink-800 hover:bg-sand-50"}`}>
                {isDelete ? <Delete className="w-6 h-6 mx-auto" strokeWidth={1.5} /> : k}
              </button>
            );
          })}
        </div>
        <div className="text-center mt-8 text-[11px] text-ink-400">
          Sarabi Beach · Servizio · v7
        </div>
      </div>
      <style>{`
        @keyframes shake {
          0%,100% { transform: translateX(0); }
          25% { transform: translateX(-8px); }
          75% { transform: translateX(8px); }
        }
      `}</style>
    </div>
  );
}
